import React, { RefObject } from 'react'
import type {
  GetSecretIdAndBeenViewedByShortIdQuery,
  PublicVideoRequest,
} from 'apollo-gql'
import cn from 'classnames'
import { useTranslation } from 'lib/i18n'
import smoothscroll from 'smoothscroll-polyfill'

import Emoji, { EmojiType } from 'components/UI/Emoji/Emoji'
import Typography from 'components/UI/Typography/Typography'
import useEffectOnce from 'hooks/useEffectOnce'
import useResponsive from 'hooks/useResponsive'
import useWindowScroll from 'hooks/useWindowScroll'

import VideoActionButtons from './VideoActionButtons'
import VideoShare from './VideoShare'

import styles from './VideoRequestPage.module.css'

type VideoInformationProps = {
  request: PublicVideoRequest
  showReviewButton: boolean
  contentRef: React.MutableRefObject<HTMLDivElement>
  review: GetSecretIdAndBeenViewedByShortIdQuery['market']['review']
  videoIsPlaying: boolean
  videoPlayerRef: RefObject<{ [key: string]: () => void } | null>
}

export default function VideoInformation({
  request,
  showReviewButton,
  contentRef,
  review,
  videoIsPlaying,
  videoPlayerRef = null,
}: VideoInformationProps): JSX.Element {
  const { t } = useTranslation()
  const { isMobile } = useResponsive()
  const windowScroll = useWindowScroll()

  useEffectOnce(() => {
    smoothscroll.polyfill()
  })

  const { publicProfile: profile } = request

  const handleClick = () => {
    const navBarHeight = 74
    const top = contentRef.current.getBoundingClientRect().top - navBarHeight

    windowScroll(top, 0)
  }

  const emoji: EmojiType = 'video-camera'

  const headerText = t(
    'VideoRequestPage.pageTitle',
    'Your video from {{name}}!',
    {
      name: profile.name,
    },
  )

  return (
    <>
      {!isMobile && (
        <div className={styles.content}>
          <Emoji emoji={emoji} className={styles.headingIcon} />

          <Typography.H1 className={styles.contentHeading}>
            {headerText}
          </Typography.H1>

          <VideoShare request={request} className={styles.videoInfoSharing} />
          <div className={styles.content}>
            <VideoActionButtons
              showReviewButton={showReviewButton}
              review={review}
              request={request}
            />
          </div>
        </div>
      )}
      {isMobile && (
        <div className={styles.orderInfoButtonWrapper}>
          <div
            className={cn(styles.content, {
              [styles.hidden]: videoIsPlaying,
            })}
          >
            <div onClick={() => videoPlayerRef?.current?.playPause()}>
              <Emoji emoji={emoji} className={styles.emoji} />
              <Typography.H3 theme="white">{headerText}</Typography.H3>
            </div>
            <div className={styles.arrowDown}>
              <button
                type="button"
                aria-label="scroll down"
                className={styles.arrowButton}
                onClick={handleClick}
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}
